// Composer modes — build-type quick actions ("Video", "Slides", "Website/App",
// "Research", "Visualization", "Write"). This is PACKAGING of existing
// capabilities, not new capability: each mode maps to tools that already ship
// today (see `capability` below). There are no dead modes.
//
// This module is intentionally framework-agnostic and dependency-free (no React,
// no icons) so it is the single source of truth for BOTH:
//   • apps/spa  — the composer UI (attaches an icon per id, swaps the placeholder
//     to `hint`, and shows a persistent selected-mode chip).
//   • apps/server — chat turn assembly injects a per-mode system hint keyed by the
//     mode `id` sent in the request body (see apps/server/src/chat/turn.ts). This
//     is the same seam the `webSearchRequested` steer uses.
//
// apps/spa resolves this via the `@/lib/composer-modes` fallback (packages/core);
// apps/server resolves the same path directly to packages/core.

export type ComposerModeId = 'video' | 'slides' | 'app' | 'research' | 'viz' | 'write';

export interface ComposerMode {
  /** Stable id. Sent in the chat request body as `mode`; keyed by the server. */
  id: ComposerModeId;
  /** Short chip / card label. */
  label: string;
  /** Placeholder text shown in the composer while this mode is active. */
  hint: string;
  /**
   * The mode directive. Injected server-side into the system prompt for the turn
   * (a concise, honest steer toward the real capability). Kept as a plain string
   * so it can also be surfaced client-side if ever needed.
   */
  scaffold: string;
  /** Honest, human-readable name of the capability/tools this mode routes to. */
  capability: string;
}

// Video is FIRST — it is the product's ace (owned scene/narrated/hyperframes
// render tiers). Order here is the render order in the UI.
export const COMPOSER_MODES: readonly ComposerMode[] = [
  {
    id: 'video',
    label: 'Video',
    hint: 'Describe the video — topic, style, and length…',
    scaffold:
      'Build this as a video using the video pipeline: prefer createSceneVideo for fast, scene-based explainers, or createNarratedVideo / hyperframes for cinematic, narrated pieces. If topic, visual style, or length are unspecified, ask briefly, then produce the video.',
    capability: 'createSceneVideo · createNarratedVideo · hyperframes',
  },
  {
    id: 'slides',
    label: 'Slides',
    hint: 'What should the deck cover? Topic and audience…',
    scaffold:
      'Build this as a slide deck / presentation using the hyperframes slideshow flow (or an HTML presentation). Produce discrete slides with a clear narrative arc, titles, and concise supporting points.',
    capability: 'hyperframes slideshow / HTML presentation',
  },
  {
    id: 'app',
    label: 'Website / App',
    hint: 'Describe the site or app you want built…',
    scaffold:
      'Build this as a working website or web app using the deployApp sandbox flow — scaffold the project, run it, and return a live preview URL the user can open. Keep the stack simple unless the user asks otherwise.',
    capability: 'deployApp sandbox flow',
  },
  {
    id: 'research',
    label: 'Research',
    hint: 'What should I research? Be specific about scope…',
    scaffold:
      'Answer this with grounded research: use webSearch / browsing to gather current sources before answering, then synthesize with inline source URLs. Do not answer from memory when facts, prices, people, or recent events may be stale.',
    capability: 'webSearch / browsing',
  },
  {
    id: 'viz',
    label: 'Visualization',
    hint: 'What data or metrics should I visualize?',
    scaffold:
      'Build this as a data visualization or dashboard using the dashboard/chart tools (create_dashboard, charts, graphs). Produce clean, clearly-labeled visuals with real structure; ask for the data if it is missing.',
    capability: 'create_dashboard / charts / graphs',
  },
  {
    id: 'write',
    label: 'Write',
    hint: 'What should I write? Article, copy, doc…',
    scaffold:
      'Produce well-structured written content (article, marketing copy, document, or plan). Focus on clear, well-organized prose; no tool calls are needed unless the user explicitly asks for media, data, or a build.',
    capability: 'plain text generation',
  },
];

export const COMPOSER_MODE_IDS: readonly ComposerModeId[] = COMPOSER_MODES.map((m) => m.id);

const COMPOSER_MODE_BY_ID: Record<string, ComposerMode> = Object.fromEntries(
  COMPOSER_MODES.map((m) => [m.id, m]),
);

/** Resolve a mode by id. Tolerant of null/undefined/unknown (returns undefined). */
export function getComposerMode(id: string | null | undefined): ComposerMode | undefined {
  if (!id) return undefined;
  return COMPOSER_MODE_BY_ID[id];
}

/** True when `id` names a real composer mode. */
export function isComposerModeId(id: string | null | undefined): id is ComposerModeId {
  return !!id && id in COMPOSER_MODE_BY_ID;
}

/**
 * Build the per-mode system-prompt section injected server-side during turn
 * assembly. Returns '' for an unknown/absent mode so callers can append blindly.
 */
export function buildComposerModeSystemHint(id: string | null | undefined): string {
  const mode = getComposerMode(id);
  if (!mode) return '';
  return [
    `## Active Build Mode: ${mode.label}`,
    `The user selected the "${mode.label}" build mode in the composer. ${mode.scaffold}`,
    `Honor this framing for the turn unless the user's message clearly asks for something else.`,
  ].join('\n');
}
